import { Document, DocumentFile } from '../../document/models/types'
import {
  DocumentFileSave,
  DocumentUpdate,
  PreviousDocumentUpdate,
  SupportingDocumentUpdate,
  TransportDocumentUpdate,
} from '../../document/models/request'

// Structural type — matches transit/ENS, house-consignment, and consignment-item
// form values. All three carry the same three document arrays.
export type FormValuesWithDocumentArrays = {
  transportDocument: { id: number | null; deleted?: boolean }[]
  previousDocument: { id: number | null; deleted?: boolean }[]
  supportingDocument: { id: number | null; deleted?: boolean }[]
}

function toFileForDeletion(file: DocumentFile): DocumentFileSave {
  return {
    id: file.id,
    fileName: file.fileName,
    documentId: file.documentId,
    mimeType: file.mimeType,
    sequenceNumber: file.sequenceNumber,
    deleted: true,
    uuid: file.uuid,
    file: file.file,
    fileBlob: file.fileBlob,
    url: file.url,
  }
}

function toDocumentUpdateForDeletion(d: Document): DocumentUpdate | null {
  const files = d.files.map(toFileForDeletion)
  const base = {
    id: d.id,
    sequenceNumber: d.sequenceNumber,
    documentType: d.documentType,
    referenceNumber: d.referenceNumber,
    deleted: true,
    files,
  }
  if (d.type === 'TRANSPORT') {
    return { ...base, type: 'TRANSPORT' } satisfies TransportDocumentUpdate
  }
  if (d.type === 'PREVIOUS') {
    return {
      ...base,
      type: 'PREVIOUS',
      complementOfInformation: d.complementOfInformation,
      transportDocumentReferenceNumber: d.transportDocumentReferenceNumber,
      procedureType: d.procedureType,
    } satisfies PreviousDocumentUpdate
  }
  if (d.type === 'SUPPORTING') {
    return {
      ...base,
      type: 'SUPPORTING',
      complementOfInformation: d.complementOfInformation,
      documentLineNumber: d.documentLineNumber,
    } satisfies SupportingDocumentUpdate
  }
  // Notes are not managed by the transit/ENS form; ignore them in the diff.
  return null
}

export default function computeConsignmentDocumentDeletes(
  snapshot: Document[],
  formValues: FormValuesWithDocumentArrays,
): DocumentUpdate[] {
  const notDeletedIds = new Set<number>()
  for (const d of formValues.transportDocument) if (d.id != null && !d.deleted) notDeletedIds.add(d.id)
  for (const d of formValues.previousDocument) if (d.id != null && !d.deleted) notDeletedIds.add(d.id)
  for (const d of formValues.supportingDocument) if (d.id != null && !d.deleted) notDeletedIds.add(d.id)

  const result: DocumentUpdate[] = []
  for (const snapshotDoc of snapshot) {
    if (snapshotDoc.type === 'NOTE') continue
    if (snapshotDoc.id == null) continue
    if (notDeletedIds.has(snapshotDoc.id)) continue
    const update = toDocumentUpdateForDeletion(snapshotDoc)
    if (update) result.push(update)
  }
  return result
}
